import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, Stack, Button, IconButton } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useSpring, animated } from 'react-spring';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import PersonIcon from '@mui/icons-material/Person';
import WomanIcon from '@mui/icons-material/Woman';
import EmailIcon from '@mui/icons-material/Email';

const testimonials = [
  {
    name: 'Michael Chen',
    company: 'SalesHive',
    role: 'VP of Sales',
    quote: 'ImpactQuill helped us fix our email reputation and reach more customers. Their team is top-notch!',
    rating: 5,
    color: '#0B60B1',
    gender: 'male'
  },
  {
    name: 'Sarah Johnson',
    company: 'SalesNexus',
    role: 'Marketing Director',
    quote: 'The DMARC and deliverability suite is a game changer. Highly recommended for any business.',
    rating: 5,
    color: '#0B60B1',
    gender: 'female'
  },
  {
    name: 'David Johnson',
    company: 'SparkPost',
    role: 'CTO',
    quote: 'We saw a 30% increase in inbox placement after working with ImpactQuill. Amazing results!',
    rating: 5,
    color: '#0B60B1',
    gender: 'male'
  },
  {
    name: 'Emily Rodriguez',
    company: 'NeoSoftware',
    role: 'Head of Growth',
    quote: 'Their expertise in email deliverability is unmatched. Our open rates have never been better.',
    rating: 5,
    color: '#0B60B1',
    gender: 'female'
  },
  {
    name: 'James Wilson',
    company: 'Drivetrain',
    role: 'CEO',
    quote: 'The team at ImpactQuill transformed our email strategy. We\'re now reaching more customers than ever.',
    rating: 5,
    color: '#0B60B1',
    gender: 'male'
  }
];

const AnimatedEmail: React.FC<{ delay: number; duration: number; startX: number; endX: number; startY: number; endY: number; size: number }> = ({ delay, duration, startX, endX, startY, endY, size }) => {
  const animation = useSpring({
    from: { x: startX, y: startY, opacity: 0, rotate: 0 },
    to: { x: endX, y: endY, opacity: 0.15, rotate: 360 },
    config: { duration },
    delay,
    loop: true,
  });

  return (
    <animated.div
      style={{
        position: 'absolute',
        transform: animation.x.to(x => `translate(${x}px, ${animation.y.get()}px) rotate(${animation.rotate.get()}deg)`),
        opacity: animation.opacity,
      }}
    >
      <EmailIcon sx={{ 
        fontSize: size,
        color: '#0B60B1',
        filter: 'drop-shadow(0 2px 4px rgba(11,96,177,0.2))',
      }} />
    </animated.div>
  );
};

const Testimonials: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const containerAnimation = useSpring({
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
    config: { tension: 280, friction: 20 }
  });

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0
    })
  };

  const paginate = (newDirection: number) => {
    setDirection(newDirection);
    setCurrentIndex((prevIndex) => (prevIndex + newDirection + testimonials.length) % testimonials.length);
  };

  useEffect(() => {
    const timer = setInterval(() => {
      paginate(1);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <Box sx={{ py: { xs: 6, md: 12 }, background: '#f7fafd', position: 'relative', overflow: 'hidden' }}>
      {/* Animated Email Icons */}
      {Array.from({ length: 15 }).map((_, index) => (
        <AnimatedEmail
          key={index}
          delay={index * 300}
          duration={4000 + Math.random() * 2000}
          startX={Math.random() * window.innerWidth}
          endX={Math.random() * window.innerWidth}
          startY={Math.random() * window.innerHeight}
          endY={Math.random() * window.innerHeight}
          size={16 + Math.random() * 24}
        />
      ))}

      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.05,
          backgroundImage: 'radial-gradient(#0B60B1 1px, transparent 1px)',
          backgroundSize: '30px 30px',
          pointerEvents: 'none',
        }}
      />

      {/* Floating Email Icons */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
        }}
      >
        {Array.from({ length: 6 }).map((_, index) => (
          <motion.div
            key={`float-${index}`}
            style={{
              position: 'absolute',
              opacity: 0.1,
            }}
            animate={{
              y: ['-100%', '100%'],
              x: `${(index * 16.6) + Math.random() * 5}%`,
            }}
            transition={{
              duration: 4 + Math.random() * 2,
              repeat: Infinity,
              delay: index * 0.8,
              ease: 'linear',
            }}
          >
            <EmailIcon sx={{ 
              fontSize: 24,
              color: '#0B60B1',
              filter: 'drop-shadow(0 2px 4px rgba(11,96,177,0.2))',
            }} />
          </motion.div>
        ))}
      </Box>

      <animated.div style={containerAnimation}>
        <Typography 
          variant="h3" 
          fontWeight={800} 
          align="center" 
          mb={2}
          sx={{ 
            background: 'linear-gradient(45deg, #0B60B1 30%, #094a8f 90%)',
            backgroundClip: 'text',
            textFillColor: 'transparent',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            position: 'relative',
            zIndex: 1,
          }}
        >
          What Our Clients Say
        </Typography>
        <Typography 
          variant="h6" 
          align="center" 
          color="text.secondary" 
          mb={6} 
          sx={{ 
            maxWidth: 600, 
            mx: 'auto',
            position: 'relative',
            zIndex: 1,
          }}
        >
          Join hundreds of satisfied customers who trust ImpactQuill for their email infrastructure
        </Typography>
      </animated.div>

      <Box sx={{ position: 'relative', maxWidth: 800, mx: 'auto', px: 2 }}>
        <Box sx={{ position: 'relative', height: 400 }}>
          <AnimatePresence initial={false} custom={direction}>
            <motion.div
              key={currentIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 }
              }}
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                zIndex: 1,
              }}
            >
              <Card 
                sx={{ 
                  height: '100%',
                  borderRadius: 4,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  overflow: 'visible',
                  position: 'relative',
                  background: 'white',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: -20,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: '#0B60B1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 4px 20px rgba(11,96,177,0.2)',
                  }}
                >
                  {testimonials[currentIndex].gender === 'male' ? (
                    <PersonIcon sx={{ width: 40, height: 40, color: 'white' }} />
                  ) : (
                    <WomanIcon sx={{ width: 40, height: 40, color: 'white' }} />
                  )}
                </Box>
                <CardContent sx={{ pt: 8, px: 4, pb: 4, height: '100%' }}>
                  <Stack spacing={3} alignItems="center" justifyContent="center" height="100%">
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {[...Array(5)].map((_, i) => (
                        i < testimonials[currentIndex].rating ? 
                          <StarIcon key={i} sx={{ color: '#0B60B1' }} /> : 
                          <StarBorderIcon key={i} sx={{ color: '#0B60B1' }} />
                      ))}
                    </Box>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontStyle: 'italic',
                        textAlign: 'center',
                        color: 'text.primary',
                        fontWeight: 500,
                        lineHeight: 1.6,
                      }}
                    >
                      "{testimonials[currentIndex].quote}"
                    </Typography>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" fontWeight={700} color="#0B60B1">
                        {testimonials[currentIndex].name}
                      </Typography>
                      <Typography variant="subtitle1" color="text.secondary">
                        {testimonials[currentIndex].role}
                      </Typography>
                      <Typography variant="subtitle2" color="text.secondary">
                        {testimonials[currentIndex].company}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </motion.div>
          </AnimatePresence>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, gap: 2 }}>
          <IconButton 
            onClick={() => paginate(-1)}
            sx={{ 
              backgroundColor: 'white',
              boxShadow: '0 2px 8px rgba(11,96,177,0.1)',
              color: '#0B60B1',
              '&:hover': { 
                backgroundColor: 'white',
                color: '#094a8f',
              }
            }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>
          <IconButton 
            onClick={() => paginate(1)}
            sx={{ 
              backgroundColor: 'white',
              boxShadow: '0 2px 8px rgba(11,96,177,0.1)',
              color: '#0B60B1',
              '&:hover': { 
                backgroundColor: 'white',
                color: '#094a8f',
              }
            }}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ textAlign: 'center', mt: 6 }}>
        <Button 
          variant="contained" 
          size="large"
          href="/case-studies"
          sx={{ 
            fontWeight: 600,
            px: 4,
            py: 1.5,
            borderRadius: 3,
            background: 'linear-gradient(45deg, #0B60B1 30%, #094a8f 90%)',
            boxShadow: '0 4px 14px rgba(11,96,177,0.2)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 20px rgba(11,96,177,0.3)',
              background: 'linear-gradient(45deg, #094a8f 30%, #0B60B1 90%)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          See More Success Stories
        </Button>
      </Box>
    </Box>
  );
};

export default Testimonials; 